import type { LandingContent } from "./types";
import controlsHero from "@/assets/heating-controls/controls-hero.webp";
import controlsDial from "@/assets/heating-controls/controls-card-dial.webp";
import controlsTrv from "@/assets/heating-controls/controls-card-trv.webp";
import controlsZoning from "@/assets/heating-controls/controls-card-zoning.webp";
import controlsImbalance from "@/assets/heating-controls/controls-card-imbalance.webp";
import controlsEmpty from "@/assets/heating-controls/controls-card-empty.webp";
import controlsStep1 from "@/assets/heating-controls/controls-step-1.webp";
import controlsEvidence from "@/assets/heating-controls/controls-evidence.webp";

/**
 * Body content for the /heating-controls page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * Heating-controls-specific bands (inspect-first, what-we-see, process,
 * controls-we-install, coordinated-measure, on-the-day, social-housing)
 * are inlined in `src/components/landing/lp-heating-controls.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 */
export const heatingControlsLp: LandingContent = {
  pageKind: "heating-controls",
  slug: "heating-controls",
  path: "/lp/heating-controls",
  title: "Heating Controls & Smart Thermostats In South Wales",
  metaTitle: "Heating Controls & Smart Thermostats South Wales | Gas Safe | CRS Energy",
  metaDescription:
"Smart thermostats, TRVs, zoning and load compensation across South Wales. Gas Safe Registered, MCS Accredited, manufacturer-backed warranty. Free assessment.",
  defaultService: "Heating Controls",
  heroHeadline: "Heating Controls & Smart Thermostats In South Wales",
  heroSubheading:
"Better comfort and control from upgraded heating controls across Wales and the South of England, installed and properly configured by qualified engineers, and set up to work with your existing boiler or heat pump.",
  heroBullets: [
"Gas Safe Registered",
"MCS Accredited",
"Manufacturer-Backed Warranty",
"Free Assessment",
"No Obligation",
  ],
  heroImage: controlsHero,
  heroAlt: "Smart heating thermostat mounted on a wall",
  heroFloatImages: [
    { src: controlsHero, slug: "controls-hero" },
    { src: controlsDial, slug: "controls-dial" },
    { src: controlsTrv, slug: "controls-trv" },
    { src: controlsZoning, slug: "controls-zoning" },
    { src: controlsImbalance, slug: "controls-imbalance" },
    { src: controlsEmpty, slug: "controls-empty" },
    { src: controlsStep1, slug: "controls-step" },
    { src: controlsEvidence, slug: "controls-evidence" },
  ],
  trustItems: [
"Gas Safe Registered",
"MCS Accredited",
"Manufacturer-Backed Warranty",
"Free Assessment",
"No Obligation",
  ],
  problemTitle: "Why Heating Controls Matter",
  problemPoints: [
    {
      title: "Comfort You Can Actually Set",
      body: "Modern controls give you accurate temperatures and schedules per room or zone, not a single dial fighting the whole house.",
    },
    {
      title: "Control From Anywhere",
      body: "App and voice control let you respond to changes in routine, weather and occupancy without standing at the boiler.",
    },
    {
      title: "Efficiency Through Proper Setup",
      body: "Correct zoning, sensible schedules and weather or load compensation help the heating system run only as hard as it needs to.",
    },
  ],
  clarityTitle: "What Good Heating Controls Look Like",
  clarityBody:
"A correctly paired thermostat, zoning that matches how the home is actually used, TRVs that move freely on every radiator, and a schedule that reflects real occupancy. Compatible with your boiler or heat pump, configured by the engineer, demonstrated to you on handover.",
  productTitle: "Controls We Install",
  productPoints: [
    {
      title: "Smart & Programmable Thermostats",
      body: "Boiler-compatible and heat-pump-compatible thermostats from leading manufacturers, paired and configured on the day.",
    },
    {
      title: "TRVs & Zoning",
      body: "Manual or smart TRVs, with zoning set up to match how the home is actually used so you're not heating empty rooms.",
    },
    {
      title: "Weather & Load Compensation",
      body: "Where the system supports it, we set up compensation so flow temperatures track outside conditions and demand.",
    },
    {
      title: "Hot Water Scheduling",
      body: "Cylinder and hot-water schedules configured around your routine, with legionella cycles where appropriate.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Assessment",
      description:
"We look at your system type, existing controls and zoning potential, and confirm what your boiler or heat pump supports.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Recommendation",
      description:
"A clear, written recommendation explaining what we'd fit, why it suits your setup, and how it will be configured.",
      icon: "file",
    },
    {
      step: 3,
      title: "Install & Configure",
      description:
"Qualified engineer installs, pairs and properly configures the controls, including zoning, schedules and compensation where supported.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Handover",
      description:
"App setup, a live demo of how to use it, and clear written notes. Manufacturer warranty registered on completion.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "What Affects Compatibility",
  qualificationItems: [
"System type (combi, system, regular boiler, or heat pump)",
"Existing wiring and programmer location",
"Hot water cylinder and how it's controlled",
"Number of zones the system can support",
"Wi-Fi coverage where the thermostat will sit",
  ],
  includedItems: [
"Free pre-installation assessment",
"Manufacturer-supplied thermostat or zoning kit",
"Qualified install, pairing and configuration",
"Schedules and zones set up to your routine",
"App setup and live handover demo",
"Manufacturer warranty registration",
"Aftercare guidance",
  ],
  trustSectionTitle: "Coordinated Heating Controls, Properly Configured",
  trustSectionBody:
"Heating controls aren't a gadget bolted on the wall. We treat them as part of a coordinated heating system, compatible with both boilers and heat pumps, installed by a Gas Safe registered engineer and configured so they actually do what the manufacturer designed them to do.",
  benefits: [
    { title: "More Even Comfort", description: "Accurate room temperatures and zoning that matches how you live." },
    { title: "Easier Control", description: "App, voice and schedule control without fighting an old dial." },
    { title: "Better System Behaviour", description: "Compensation and schedules help the heating run only as hard as it needs to." },
    { title: "Documented Install", description: "Manufacturer warranty registered and handover notes provided." },
  ],
  testimonialsFull: [
    {
      name: "Stephen D",
      date: "October 2025",
      serviceTag: "Loft Insulation, Fan & Radiator Controls",
      source: "google",
      quote:
        "Recently had loft insulation, kitchen fan and new radiator controls fitted. All work completed within 2 days to a high standard. Workmen were fast, efficient, polite and helpful. Similarly whenever I spoke to their office staff they were both helpful and polite. I was messaged prior to each job informing me of the name and ETA of workmen. Would not hesitate to recommend.",
    },
    {
      name: "Bin Li",
      date: "May 2026",
      serviceTag: "Heating Upgrade",
      source: "trustpilot",
      quote:
        "My recent experience with CRS Energy was excellent. What stood out was the install team, admin staff, scheduling, friendly service, tidy and clean, communication, on time, quality of work and value for money, and how they explained things clearly. Professional and friendly team, excellent work. We can feel warm during the winter. Very satisfied!",
    },
    {
      name: "Jan",
      date: "June 2026",
      serviceTag: "Home Energy Upgrades",
      source: "trustpilot",
      quote:
        "Friendly, helpful and knowledgeable office and practical field staff made the whole process easy to understand and navigate from the beginning. On-site workers were hugely respectful of our property and got all jobs completed on exact appointment days and within allocated work times. A great service all round from CRS. Highly recommend.",
    },
  ],
  faqs: [
    {
      question: "Will a smart thermostat work with my boiler or heat pump?",
      answer:
"Most modern combi, system and regular boilers, and most current heat pumps, support at least one suitable smart thermostat. We confirm compatibility during the assessment before recommending anything, and we only quote controls that the manufacturer supports for your system.",
    },
    {
      question: "Do I need zoning?",
      answer:
"Not every home benefits from full zoning. If different parts of the house are used at very different times or have very different heating demands, zoning can help. We'll tell you honestly when zoning is worth it and when better TRVs plus a sensible schedule will do the job.",
    },
    {
      question: "Can you set up the app for me?",
      answer:
"Yes. App setup, account creation and pairing are part of the install. We demo it with you before we leave so you can actually use it from day one.",
    },
    {
      question: "Will smart controls save me money?",
      answer:
"Correctly configured controls help the heating run only when and where it's needed, which generally reduces unnecessary runtime. Real savings depend on your habits, fabric, heat source and how the controls are used, so we describe the benefits qualitatively rather than quoting a £ figure.",
    },
    {
      question: "Do you supply the thermostats or do I buy my own?",
      answer:
"We supply, install and configure manufacturer-approved controls so they're covered by the manufacturer warranty and properly paired with your system. If you've already bought something specific, we'll check compatibility before we commit to fitting it.",
    },
    {
      question: "Do you offer landlord-suitable options?",
      answer:
"Yes. For HMOs, social housing and rental properties we can fit tamper-resistant or simplified controls, provide tenant guidance, and supply completion documentation suitable for landlords and managing agents.",
    },
  ],
  ctaTitle: "Book Your Free Heating Controls Assessment",
  sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
